import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Users, Wallet, ArrowUpCircle, FileCheck, TrendingUp, UserPlus, Loader2 } from 'lucide-react'
import { getAdminStats, AdminStats, formatCurrency } from '@/features/admin/services/adminService'

export default function AdminDashboard() {
  const { t } = useTranslation()
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    async function loadStats() {
      setIsLoading(true)
      const data = await getAdminStats()
      setStats(data)
      setIsLoading(false)
    }
    loadStats()
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-casino-gold" />
      </div>
    )
  }

  const statCards = [
    {
      title: t('admin.totalUsers'),
      value: stats?.totalUsers || 0,
      icon: Users,
      color: 'text-blue-400',
    },
    {
      title: t('admin.deposits') + ' ' + t('admin.pending'),
      value: stats?.pendingDeposits || 0,
      icon: Wallet,
      color: 'text-green-400',
      alert: (stats?.pendingDeposits || 0) > 0,
    },
    {
      title: t('admin.withdrawals') + ' ' + t('admin.pending'),
      value: stats?.pendingWithdrawals || 0,
      icon: ArrowUpCircle,
      color: 'text-yellow-400',
      alert: (stats?.pendingWithdrawals || 0) > 0,
    },
    {
      title: t('admin.pendingKyc'),
      value: stats?.pendingKyc || 0,
      icon: FileCheck,
      color: 'text-purple-400',
      alert: (stats?.pendingKyc || 0) > 0,
    },
    {
      title: t('admin.memberships') + ' ' + t('admin.pending'),
      value: stats?.pendingMemberships || 0,
      icon: UserPlus,
      color: 'text-pink-400',
      alert: (stats?.pendingMemberships || 0) > 0,
    },
    {
      title: t('admin.totalDeposits'),
      value: formatCurrency(stats?.totalDeposits || 0),
      icon: TrendingUp,
      color: 'text-casino-gold',
      isAmount: true,
    },
  ]

  return (
    <div className="space-y-6">
      <h1 className="font-cinzel text-2xl text-casino-gold">{t('admin.dashboard')}</h1>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon
          return (
            <Card key={index} className={stat.alert ? 'border-yellow-500/50' : ''}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-400">
                  {stat.title}
                </CardTitle>
                <Icon className={`h-5 w-5 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${stat.alert ? 'text-yellow-400' : 'text-white'}`}>
                  {stat.isAmount ? stat.value : stat.value}
                </div>
                {stat.alert && (
                  <p className="text-xs text-yellow-500 mt-1">Requiere atención</p>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Resumen Financiero</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-400">Total Depositado:</span>
              <span className="text-green-400 font-bold">
                {formatCurrency(stats?.totalDeposits || 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Total Retirado:</span>
              <span className="text-red-400 font-bold">
                {formatCurrency(stats?.totalWithdrawals || 0)}
              </span>
            </div>
            <div className="flex justify-between border-t border-zinc-700 pt-2 mt-2">
              <span className="text-gray-400">Balance Neto:</span>
              <span className="text-casino-gold font-bold">
                {formatCurrency((stats?.totalDeposits || 0) - (stats?.totalWithdrawals || 0))}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Acciones Pendientes</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {(stats?.pendingDeposits || 0) > 0 && (
                <li className="flex items-center gap-2 text-yellow-400">
                  <Wallet className="w-4 h-4" />
                  {stats?.pendingDeposits} depósito(s) por aprobar
                </li>
              )}
              {(stats?.pendingWithdrawals || 0) > 0 && (
                <li className="flex items-center gap-2 text-yellow-400">
                  <ArrowUpCircle className="w-4 h-4" />
                  {stats?.pendingWithdrawals} retiro(s) por aprobar
                </li>
              )}
              {(stats?.pendingKyc || 0) > 0 && (
                <li className="flex items-center gap-2 text-yellow-400">
                  <FileCheck className="w-4 h-4" />
                  {stats?.pendingKyc} KYC por revisar
                </li>
              )}
              {(stats?.pendingMemberships || 0) > 0 && (
                <li className="flex items-center gap-2 text-yellow-400">
                  <UserPlus className="w-4 h-4" />
                  {stats?.pendingMemberships} membresía(s) pendiente(s)
                </li>
              )}
              {!stats?.pendingDeposits &&
                !stats?.pendingWithdrawals &&
                !stats?.pendingKyc &&
                !stats?.pendingMemberships && (
                  <li className="text-green-400">Todo al día</li>
                )}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
